import { useState, useCallback, useEffect } from 'react';
import type { CheckoutUser } from '../../types/checkout';

interface UserDataSectionProps {
  user: CheckoutUser;
  onSuccess: (user: CheckoutUser) => void;
  updateUser: (data: Partial<CheckoutUser>) => Promise<{ success: boolean; message: string; user?: CheckoutUser }>;
  isLoading: boolean;
}

const cleanField = (val?: string) => val && !val.includes('@') ? val : '';

export function UserDataSection({ user, onSuccess, updateUser, isLoading }: UserDataSectionProps) {
  const [formData, setFormData] = useState({
    name: cleanField(user.name),
    lastname: cleanField(user.lastname),
    dni: user.dni || '',
    cellphone: user.cellphone || '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      name: cleanField(user.name),
      lastname: cleanField(user.lastname),
      dni: user.dni || '',
      cellphone: user.cellphone || '',
    });
  }, [user]);

  const handleChange = useCallback((field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target?.value ?? '' }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) { setError('El nombre es requerido'); return; }
    if (!formData.lastname.trim()) { setError('El apellido es requerido'); return; }
    if (!formData.cellphone.trim()) { setError('El telefono es requerido'); return; }

    const result = await updateUser(formData);

    if (result.success && result.user) {
      onSuccess(result.user);
    } else {
      setError(result.message);
    }
  }, [formData, updateUser, onSuccess]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <p className="text-sm text-gray-500">Completa tus datos para continuar</p>

      <div className="grid grid-cols-2 gap-3 max-[480px]:grid-cols-1">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="checkout-name" className="text-sm font-medium text-gray-800">Nombre *</label>
          <input
            type="text" id="checkout-name"
            className="rounded-lg border border-gray-300 px-3.5 py-3 text-base transition-colors focus:border-green-500 focus:outline-none disabled:bg-gray-100"
            value={formData.name} onChange={handleChange('name')}
            placeholder="Juan" autoComplete="given-name" disabled={isLoading}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="checkout-lastname" className="text-sm font-medium text-gray-800">Apellido *</label>
          <input
            type="text" id="checkout-lastname"
            className="rounded-lg border border-gray-300 px-3.5 py-3 text-base transition-colors focus:border-green-500 focus:outline-none disabled:bg-gray-100"
            value={formData.lastname} onChange={handleChange('lastname')}
            placeholder="Perez" autoComplete="family-name" disabled={isLoading}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="checkout-cellphone" className="text-sm font-medium text-gray-800">Teléfono *</label>
        <input
          type="tel" id="checkout-cellphone"
          className="rounded-lg border border-gray-300 px-3.5 py-3 text-base transition-colors focus:border-green-500 focus:outline-none disabled:bg-gray-100"
          value={formData.cellphone} onChange={handleChange('cellphone')}
          placeholder="Ej: 3512395004" autoComplete="tel" disabled={isLoading}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="checkout-dni" className="text-sm font-medium text-gray-800">DNI (opcional)</label>
        <input
          type="text" id="checkout-dni"
          className="rounded-lg border border-gray-300 px-3.5 py-3 text-base transition-colors focus:border-green-500 focus:outline-none disabled:bg-gray-100"
          value={formData.dni} onChange={handleChange('dni')}
          placeholder="12345678" autoComplete="off" disabled={isLoading}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{error}</div>
      )}

      <button
        type="submit"
        className="rounded-lg bg-green-500 px-5 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
        disabled={isLoading || !formData.name.trim() || !formData.lastname.trim() || !formData.cellphone.trim()}
      >
        {isLoading ? 'Guardando...' : 'Continuar'}
      </button>
    </form>
  );
}
